import { 
  getParentOriginFromUrl, 
  loadCachedToken, 
  cacheToken, 
  clearCachedToken 
} from '../utils/authUtils';

/**
 * Initialize authentication before React app mounts
 * This runs synchronously in index.js
 */
export function initializeAuth() {
  // Initialize auth data globally
  window.__authTokenData__ = window.__authTokenData__ || null;
  window.__authError__ = window.__authError__ || null;

  // First, try to use cached token
  if (loadCachedToken()) {
    console.log('Token loaded from cache');
    return; // Token ready, React app can use it
  }

  // No cached token, request from parent
  if (!window.opener) {    
    const parentOrigin = getParentOriginFromUrl() || 'main application';
    window.__authError__ = `Please login on ${parentOrigin} and relaunch your chat application. Your website login session has been expired.`;
    return;
  }

  // Get parent origin from URL parameter
  const parentOrigin = getParentOriginFromUrl();
  
  if (!parentOrigin) {
    console.error('Unable to determine parent origin');
    window.__authError__ = 'Please login on the main application and relaunch your chat application. Your login session has been expired.';
    return;
  }

   
  
  // Set up timeout for parent response
  const timeout = setTimeout(() => {
    if (!window.__authTokenData__ && !window.__authError__) {
      console.error('Token request timeout - no response from parent');
      window.__authError__ = `Please login on ${parentOrigin} and relaunch your chat application. Your parent origin login session has been expired or is not responding.`;
      window.dispatchEvent(new CustomEvent('authTimeout'));
    }
  }, 10000); // 10 second timeout

  // Listen for postMessage from parent
  const handleMessage = (event) => {
    console.log('Message received from:', event.origin, 'Expected:', parentOrigin);
    
    // Helper function to check if origin is allowed
   // Helper function to check if origin is allowed
const isOriginAllowed = (origin, expectedOrigin) => {
  // Allow exact match with expected parent origin
  if (origin === expectedOrigin) {
    return true;
  }
   const isFeatureEventScribeNetDev = expectedOrigin === 'eventscribenet.dev' || 
                                   expectedOrigin.endsWith('.eventscribenet.dev');
                                   if(isFeatureEventScribeNetDev) return true;
  // Allow eventscribenet.dev and eventscribe.net patterns
  try {
    const url = new URL(origin);
    // Check if it's HTTPS and matches allowed domains
    if (url.protocol === 'https:') {
      const hostname = url.hostname;
      
      // Check for eventscribenet.dev domain
      const isEventScribeNetDev = hostname === 'eventscribenet.dev' || 
                                   hostname.endsWith('.eventscribenet.dev');
      
      // Check for eventscribe.net domain
      const isEventScribeNet = hostname === 'eventscribe.net' || 
                               hostname.endsWith('.eventscribe.net');
      
      if (isEventScribeNetDev || isEventScribeNet) {
        return true;
      }
    }
  } catch (e) {
    // Invalid URL
    return false;
  }
  
  return false;
};
  // RE-ENABLE origin verification for security
  if (!isOriginAllowed(event.origin, parentOrigin)) {
    console.warn('Message from unauthorized origin:', event.origin, 'Expected:', parentOrigin);
    return;
  }
  
    
    clearTimeout(timeout);

    // Receive token from parent
    if (event.data?.action === 'token') {
      console.log('Token received from parent');
      window.__authTokenData__ = event.data;
      
      // Cache token in localStorage
      cacheToken(event.data);
      
      // Trigger event for AuthGuard to pick up
      window.dispatchEvent(new CustomEvent('authTokenReceived', { 
        detail: event.data 
      }));
      
      // Clean up listener after receiving token
      window.removeEventListener('message', handleMessage);
    }
    
    // Receive error from parent (401 unauthorized)
    if (event.data?.action === 'error') {
      console.error('Authentication error:', event.data.message);
       // Custom error message based on error type
      let errorMessage = `Please login on ${parentOrigin} and relaunch your chat application. Your parent origin login session has been expired.`;
      
      // Check if it's a specific authentication error
      if (event.data.message && event.data.message.includes('401')) {
        errorMessage = `Authentication failed. Please login on ${parentOrigin} and relaunch your chat application. Your parent origin login session has been expired.`;
      } else if (event.data.message && event.data.message.includes('403')) {
        errorMessage = `Access denied. Please login on ${parentOrigin} and relaunch your chat application. Your parent origin login session has been expired.`;
      } else if (event.data.message && event.data.message.includes('token')) {
        errorMessage = `Token validation failed. Please login on ${parentOrigin} and relaunch your chat application. Your parent origin login session has been expired.`;
      }
      
      window.__authError__ = errorMessage;
      
      // Clear any cached token on auth error
      clearCachedToken();
      
      // Trigger event for AuthGuard to pick up
      window.dispatchEvent(new CustomEvent('authError', { 
        detail: event.data.message 
      }));
      
      // Clean up listener after receiving error
      window.removeEventListener('message', handleMessage);
    }

    // **NEW: Handle logout message from parent**
    if (event.data?.action === 'logout') {
        console.log('Logout message received from parent:', event.data.reason);
        
        // Clear cached authentication data
        clearCachedToken();
        window.__authTokenData__ = null;
        // Set custom logout message
        const logoutMessage = `Session ended. Please login on ${parentOrigin} and relaunch your chat application. Your parent origin login session has been expired.`;
        window.__authError__ = logoutMessage;
        
        // Dispatch logout event for your app components to handle
        window.dispatchEvent(new CustomEvent('parentLogout', { 
            detail: { 
                reason: event.data.reason,
                message: event.data.message,
                 parentOrigin: parentOrigin
            }
        }));
        
        // Optional: Show user notification
        console.log('Main application session ended. Chat will close shortly.');
        
        // Clean up listener
        window.removeEventListener('message', handleMessage);
        
        // Optional: Close window after a brief delay (parent will also close it)
        setTimeout(() => {
            if (window.opener && !window.opener.closed) {
                window.close();
            }
        }, 2000);
    }
  };

  

  window.addEventListener('message', handleMessage);

  // Request token from parent
  console.log('Requesting token from parent window:', parentOrigin);
  window.opener.postMessage({
    action: 'requestToken'
  }, parentOrigin);
}